/**
 * NGX ETF catalogue for NigaETF Trader.
 *
 * Pilot universe of NGX-listed exchange-traded funds. In production this
 * catalogue streams from the BAL /v1/instruments/etfs endpoint which mirrors
 * the NGX ETF market feed. Each row carries the data needed to compute
 * premium/discount to NAV, tracking error, and yield.
 */

export type EtfCategory = 'Equity' | 'Fixed Income' | 'Commodity' | 'Sharia' | 'Sector' | 'Broad Market';

export interface EtfInstrument {
  code: string;              // NGX ticker
  name: string;
  manager: string;           // issuing fund manager
  category: EtfCategory;
  benchmark: string;         // tracked index
  lastPriceNaira: number;    // last traded price per unit
  navNaira: number;          // indicative NAV per unit
  ter: number;               // total expense ratio % (annual)
  aumBillions: number;       // AUM in NGN billions
  inceptionDate: string;     // ISO
  yieldPct?: number;         // trailing 12m distribution yield
  trackingError?: number;    // annualized tracking error %
  creationUnit: number;      // units in a creation basket (for APs)
  minLotUnits: number;       // retail minimum lot (usually 1)
  isShariaCompliant?: boolean;
  description: string;
}

export const ETFS: EtfInstrument[] = [
  { code: 'VETGRIF30',   name: 'Vetiva Griffin 30 ETF',               manager: 'Vetiva Fund Managers',    category: 'Broad Market',
    benchmark: 'NGX 30 Index', lastPriceNaira: 38.55, navNaira: 38.40, ter: 0.65, aumBillions: 2.8,
    inceptionDate: '2013-12-19', yieldPct: 4.2, trackingError: 0.55, creationUnit: 50000, minLotUnits: 1,
    description: 'Tracks the NGX 30 Index — the 30 largest NGX-listed equities by market cap.' },
  { code: 'STANBICETF30',name: 'Stanbic IBTC ETF 30',                  manager: 'Stanbic IBTC Asset Mgmt', category: 'Broad Market',
    benchmark: 'NGX 30 Index', lastPriceNaira: 118.70, navNaira: 118.50, ter: 0.80, aumBillions: 5.6,
    inceptionDate: '2014-06-25', yieldPct: 3.9, trackingError: 0.48, creationUnit: 100000, minLotUnits: 1,
    description: 'Large-cap NGX 30 tracker; Stanbic IBTC-managed, quarterly rebalanced.' },
  { code: 'NEWGOLD',     name: 'NewGold ETF',                          manager: 'Absa Fund Managers',      category: 'Commodity',
    benchmark: 'Gold Bullion (USD)', lastPriceNaira: 58200, navNaira: 58150, ter: 0.40, aumBillions: 42.1,
    inceptionDate: '2011-12-15', trackingError: 0.22, creationUnit: 10000, minLotUnits: 1,
    description: 'Physical gold-backed; each unit = 1/100 troy ounce. Hedge against NGN devaluation.' },
  { code: 'LOTUSHAL15',  name: 'Lotus Halal Equity ETF',                manager: 'Lotus Capital',           category: 'Sharia',
    benchmark: 'NGX-Lotus Islamic Index', lastPriceNaira: 14.25, navNaira: 14.20, ter: 1.25, aumBillions: 1.1,
    inceptionDate: '2014-10-30', yieldPct: 0, trackingError: 0.65, creationUnit: 50000, minLotUnits: 1,
    isShariaCompliant: true, description: 'Halal-screened NGX equities; no interest-bearing or prohibited sectors.' },
  { code: 'VETBANK',     name: 'Vetiva Banking Value ETF',              manager: 'Vetiva Fund Managers',    category: 'Sector',
    benchmark: 'NGX Banking Index', lastPriceNaira: 7.85, navNaira: 7.82, ter: 0.75, aumBillions: 1.4,
    inceptionDate: '2014-01-27', yieldPct: 8.2, trackingError: 0.72, creationUnit: 50000, minLotUnits: 1,
    description: 'Concentrated exposure to NGX-listed banks; high-yield income proxy.' },
  { code: 'VETCONGOODS', name: 'Vetiva Consumer Goods ETF',             manager: 'Vetiva Fund Managers',    category: 'Sector',
    benchmark: 'NGX Consumer Goods Index', lastPriceNaira: 6.40, navNaira: 6.38, ter: 0.75, aumBillions: 0.9,
    inceptionDate: '2014-01-27', yieldPct: 5.1, trackingError: 0.81, creationUnit: 50000, minLotUnits: 1,
    description: 'Consumer staples + FMCG basket. Nestle, Dangote Sugar, PZ, Unilever weighted.' },
  { code: 'VETINDETF',   name: 'Vetiva Industrial Goods ETF',           manager: 'Vetiva Fund Managers',    category: 'Sector',
    benchmark: 'NGX Industrial Goods Index', lastPriceNaira: 26.80, navNaira: 26.72, ter: 0.75, aumBillions: 1.2,
    inceptionDate: '2014-06-26', yieldPct: 3.4, trackingError: 0.69, creationUnit: 50000, minLotUnits: 1,
    description: 'Cement + construction materials + packaging. Dangote Cement, BUA, Lafarge.' },
  { code: 'GREENWETF',   name: 'Greenwich Alpha ETF',                   manager: 'Greenwich Asset Mgmt',    category: 'Broad Market',
    benchmark: 'NGX ASI', lastPriceNaira: 95.20, navNaira: 94.85, ter: 1.00, aumBillions: 0.7,
    inceptionDate: '2020-09-01', yieldPct: 2.8, trackingError: 1.12, creationUnit: 25000, minLotUnits: 1,
    description: 'Active tilt toward NGX ASI heavyweights; rebalanced semi-annually.' },
  { code: 'MERISTEM-50', name: 'Meristem Growth ETF (NGX 50)',          manager: 'Meristem Wealth Mgmt',    category: 'Broad Market',
    benchmark: 'NGX 50 Index', lastPriceNaira: 22.10, navNaira: 22.05, ter: 0.90, aumBillions: 1.6,
    inceptionDate: '2018-11-16', yieldPct: 3.1, trackingError: 0.58, creationUnit: 50000, minLotUnits: 1,
    description: 'Broad NGX 50 exposure; mid-cap tilt compared to NGX 30.' },
  { code: 'VSPBONDETF',  name: 'Vetiva S&P Nigerian Sovereign Bond ETF', manager: 'Vetiva Fund Managers',   category: 'Fixed Income',
    benchmark: 'S&P Nigeria Sovereign Bond Index', lastPriceNaira: 298.50, navNaira: 298.20, ter: 0.50, aumBillions: 3.2,
    inceptionDate: '2017-07-05', yieldPct: 13.6, trackingError: 0.31, creationUnit: 10000, minLotUnits: 1,
    description: 'FGN bond basket in a single unit; duration ~6y. Monthly income distributions.' },
  { code: 'SIAVEST',     name: 'SIA Vestor Bond ETF',                   manager: 'SIA Capital',             category: 'Fixed Income',
    benchmark: 'FGN 3-5y Curve', lastPriceNaira: 116.40, navNaira: 116.25, ter: 0.45, aumBillions: 2.0,
    inceptionDate: '2019-02-12', yieldPct: 14.2, trackingError: 0.28, creationUnit: 10000, minLotUnits: 1,
    description: 'Short-duration FGN bond ETF. Lower interest-rate risk than long-duration peers.' },
];

export function etfByCode(code: string): EtfInstrument | undefined {
  return ETFS.find((e) => e.code === code);
}

/** Premium (+) or discount (-) vs NAV, in %. */
export function premiumToNav(e: EtfInstrument): number {
  if (e.navNaira <= 0) return 0;
  return ((e.lastPriceNaira - e.navNaira) / e.navNaira) * 100;
}

/** Deterministic per-symbol live-quote nudge. */
export function nudgedEtfPrice(e: EtfInstrument): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < e.code.length; i++) h = Math.imul(h ^ e.code.charCodeAt(i), 16777619) >>> 0;
  const delta = ((h % 1000) / 1000) - 0.5;
  return +(e.lastPriceNaira * (1 + delta * 0.01)).toFixed(2);
}
